import type { MouseEvent } from 'react'
import { BiShapeTriangle } from 'react-icons/bi'
import TaActionButton from '../TaActionButton'
import MiniProgressDot from '../MiniProgressDot'
import { useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { useQuestionProcessing } from '../../services/TaskManagerService'
import { runTrace } from '../../services/QuestionActions'
import type { Question } from '../../types'
import { hasAnswerText } from '../../common/common'

interface Props {
  question: Question
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  testId?: string
  // When set, the button disables once a trace already exists.
  // Used in surfaces where re-tracing isn't a default affordance —
  // the user has to go into the question's tab to explicitly retrace.
  disableIfPresent?: boolean
}

// Row-sized variant of GenerateTraceButton. Renders a MiniProgressDot
// showing the question's current trace similarity (sim%), with a
// spinner overlay while a trace run is in flight. Disables when there's
// no answer to trace or the question's mid-stream. Click fires
// `runTrace` for just this question.
export default function GenerateTraceMiniDot({
  question, onMouseEnter, onMouseLeave, testId, disableIfPresent,
}: Props) {
  const { updateQuestion, getCurrentProject } = useAppActions()
  const processing = useQuestionProcessing(question.questionsha)
  const hasAnswer = hasAnswerText(question)
  const queued = processing.isqueuedtrace
  const running = processing.isrunningtrace
  // `simint >= 0` = traced; -1 / undefined = not generated yet.
  // Distinguishes "no trace yet" (grey, ready to click) from
  // "traced and found 0% similarity" (red bottom-band).
  const rawSim = (question.answerobj as any)?.simint
  const sim: number | null = typeof rawSim === 'number' && rawSim >= 0
    ? rawSim
    : null
  const disabled =
    !hasAnswer
    || processing.isprocessingtrace
    || (disableIfPresent && sim !== null)

  const handleClick = async (e?: MouseEvent) => {
    e?.stopPropagation()
    const project = getCurrentProject()
    if (!project) return
    const r = await runTrace(question, project, updateQuestion)
    if (r.success) addToast('success', 'Trace generated')
    else addToast('error', r.msg ?? 'Failed to generate trace')
  }

  return (
    <TaActionButton
      title={queued ? 'Queued' : 'Generate Trace'}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      running={running}
      queued={queued}
      iconSize={18}
      icon={<MiniProgressDot value={sim} icon={<BiShapeTriangle size={13} />} />}
      sx={{ p: 0.25, flexShrink: 0 }}
      testId={testId}
    />
  )
}
